import { Modal } from "antd";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import useAlert from "hooks/useAlert";
import styles from "./BulkEquipmentUploadModal.module.scss";
import BulkEquipmentUploadModalFooter from "./BulkEquipmentUploadModalFooter";

interface BulkEquipmentUploadModalProps {
    modalIsOpen: boolean;
    onCancel: () => void;
    submitBulkEquipmentSurvey: (file: File) => void;
}

function BulkEquipmentUploadModal({ modalIsOpen, onCancel, submitBulkEquipmentSurvey }: BulkEquipmentUploadModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const alert = useAlert();

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        }
    };

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
        accept: { "text/csv": [".csv"] },
        maxFiles: 1,
    });

    const removeFile = () => {
        setFile(null);
    };

    const modalSubmitHandler = () => {
        if (!file) return;
        submitBulkEquipmentSurvey(file);
        alert.success("Your CSV is being processed. You'll receive an email when the bulk upload is complete.");
        setFile(null);
        onCancel();
    };

    const handleCancel = () => {
        setFile(null);
        onCancel();
    };

    return (
        <Modal
            open={modalIsOpen}
            onCancel={handleCancel}
            title="Bulk equipment upload"
            footer={
                <BulkEquipmentUploadModalFooter
                    onSave={modalSubmitHandler}
                    onCancel={handleCancel}
                    isLoading={false}
                    disabled={!file}
                />
            }
        >
            <div
                {...getRootProps()}
                className={styles.dropzone}
                style={{
                    backgroundColor: isDragActive ? "#e6f7ff" : "transparent",
                }}
            >
                <input {...getInputProps()} />
                {isDragActive ? <p>Drop the file here...</p> : <p>Drag & drop a CSV file, or click to select it.</p>}
            </div>

            {fileRejections.length > 0 && (
                <div className={styles.error}>
                    {fileRejections.map(({ file: rejected }) => (
                        <p key={rejected.name}>{rejected.name}: File exceeds 20GB limit</p>
                    ))}
                </div>
            )}

            {file && (
                <div style={{ marginTop: "16px" }}>
                    <p style={{ fontWeight: 500, marginBottom: "8px" }}>Selected file</p>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        <li className={styles.uploadedFile}>
                            <span>{file.name}</span>
                            <button type="button" onClick={removeFile} className={styles.removeButton}>
                                Remove
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </Modal>
    );
}

export default BulkEquipmentUploadModal;
